import { Trophy, BarChart3, MessageSquareText, EyeOff, CalendarClock } from "lucide-react";
import type { ScheduleWithStudy, Study } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { formatModelSummary } from "@/lib/format";
import { formatCadence } from "./SchedulePanel";

const typeIcons = {
  tournament: Trophy,
  scoring: BarChart3,
  sentiment: MessageSquareText,
  blind_query: EyeOff,
};

const typeLabels: Record<Study["type"], string> = {
  tournament: "Tournament",
  scoring: "Scoring",
  sentiment: "Sentiment",
  blind_query: "Blind Query",
};

interface StudyCardProps {
  study: Study;
  schedule?: ScheduleWithStudy;
  onClick: () => void;
}

export function StudyCard({ study, schedule, onClick }: StudyCardProps) {
  const Icon = typeIcons[study.type] || BarChart3;
  const studyModels = study.models?.length ? study.models : [study.model];
  const brandSummary = study.tracked_brand_source === "workspace_all" && study.brand_ids.length === 0
    ? "All workspace brands"
    : `${study.brand_ids.length} brand${study.brand_ids.length === 1 ? "" : "s"}`;

  return (
    <Card
      className="cursor-pointer hover:border-brand-200 transition-colors"
      onClick={onClick}
    >
      <CardContent className="py-5 px-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-brand-50 border border-brand-100 flex items-center justify-center">
              <Icon className="w-4 h-4 text-brand-600" />
            </div>
            <div>
              <h3 className="text-[14px] font-semibold text-brand-950">{study.name}</h3>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {brandSummary} &middot; {formatModelSummary(studyModels)}
              </p>
            </div>
          </div>
          <Badge variant={study.type === "blind_query" ? "default" : "muted"}>{typeLabels[study.type]}</Badge>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[12px] text-slate-400">
            Created {new Date(study.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </p>
          {schedule && (
            <Badge variant={schedule.enabled ? "success" : "muted"} className="flex items-center gap-1">
              <CalendarClock className="w-3 h-3" />
              {schedule.enabled ? formatCadence(schedule) : "Paused"}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
